import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Copy, Download } from "lucide-react";
import { downloadFileLink, rpc, useAction } from "@vixio/data";
import { Button, Meter, StatusFromMap, formatDate, todayIso, useToast } from "@vixio/ui";
import type { ClientPhase } from "./api";

/** Bloco com título do portal (mais respiro que a Section do painel). */
export function Block({ title, actions, children, id }: { title: ReactNode; actions?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="vx-block" aria-labelledby={id}>
      <div className="vx-block__head">
        <h2 className="vx-block__title" id={id}>
          {title}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

/** Link com aparência de botão (navegação, não ação). */
export function LinkButton({ to, children, primary, small }: { to: string; children: ReactNode; primary?: boolean; small?: boolean }) {
  return (
    <Link to={to} className={`vx-btn${primary ? " vx-btn--primary" : ""}${small ? " vx-btn--sm" : ""}`}>
      {children}
    </Link>
  );
}

export type StepTone = "accent" | "warn" | "crit" | "ok";

export function NextStep({ tone = "accent", icon, title, desc, action }: {
  tone?: StepTone;
  icon: ReactNode;
  title: ReactNode;
  desc?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <li className={`vx-next__item${tone === "accent" ? "" : ` vx-next__item--${tone}`}`}>
      <span className="vx-next__icon" aria-hidden="true">
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <p className="vx-next__title">{title}</p>
        {desc ? <p className="vx-next__desc">{desc}</p> : null}
      </div>
      {action ? <div className="vx-next__action">{action}</div> : null}
    </li>
  );
}

/** Código para copiar (PIX copia e cola, linha digitável). */
export function CopyField({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <div className="vx-stack" style={{ gap: 6 }}>
      <div className="vx-row vx-row--between">
        <span className="text-2" style={{ fontSize: "var(--fs-13)" }}>
          {label}
        </span>
        <Button
          size="sm"
          icon={<Copy />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              toast.ok("Copiado", label);
            } catch {
              toast.error("Não foi possível copiar", "Selecione o texto e copie manualmente.");
            }
          }}
        >
          Copiar
        </Button>
      </div>
      <p className="vx-code">{value}</p>
    </div>
  );
}

export function Progress({ pct, label }: { pct: number; label?: ReactNode }) {
  const v = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className="vx-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={v} aria-label="Progresso">
      <div className="vx-progress__label">
        <span>{label ?? "Progresso"}</span>
        <span className="num">{v}%</span>
      </div>
      <Meter pct={v} />
    </div>
  );
}

const PHASE_STATUS = {
  pending: { label: "a fazer", tone: "neutral" as const },
  active: { label: "em andamento", tone: "info" as const },
  done: { label: "concluída", tone: "ok" as const },
};

export function PhaseList({ phases }: { phases: ClientPhase[] }) {
  if (phases.length === 0) return <p className="muted">O cronograma aparece aqui quando a equipe definir as fases.</p>;
  return (
    <ol className="vx-phases">
      {phases.map((ph) => (
        <li key={ph.id} className={`vx-phase vx-phase--${ph.status}`}>
          <span className="vx-phase__mark" aria-hidden="true" />
          <div style={{ minWidth: 0 }}>
            <p className="vx-phase__name">{ph.name}</p>
            <p className="vx-phase__meta">
              {ph.start_date || ph.due_date ? `${formatDate(ph.start_date)} a ${formatDate(ph.due_date)}` : "datas a definir"}
              {ph.tasks_total > 0 ? ` · ${ph.tasks_done} de ${ph.tasks_total} atividades` : ""}
              {ph.late ? " · atrasada" : ""}
            </p>
          </div>
          <StatusFromMap map={PHASE_STATUS} value={ph.status} />
        </li>
      ))}
    </ol>
  );
}

/** Dias até a data (negativo: já passou), no fuso do app. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const a = Date.parse(`${todayIso()}T00:00:00Z`);
  const b = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/** Texto com links http(s) clicáveis (mensagens de chamado). Nada de HTML vindo do banco. */
export function Linkified({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return (
    <>
      {parts.map((p, i) =>
        /^https?:\/\/[^\s]+$/.test(p) ? (
          <a key={i} href={p} target="_blank" rel="noopener noreferrer nofollow">
            {p}
          </a>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/**
 * "Baixar PDF" de um item do portal (contrato, aditivo, certificado, documento): portal.document_file confere o acesso
 * à conta e libera uma URL assinada curta; o contrato sai assinado (carimbo e página de evidências) quando já selado.
 */
export function PdfButton({ itemId, small, primary, label = "Baixar PDF" }: { itemId: string; small?: boolean; primary?: boolean; label?: string }) {
  const download = useAction(async () => downloadFileLink(await rpc("portal", "document_file", { p_item: itemId })), {
    success: null,
    errorTitle: "Não foi possível baixar o PDF",
  });
  return (
    <Button size={small ? "sm" : "md"} variant={primary ? "primary" : "secondary"} icon={<Download />} loading={download.isPending} onClick={() => download.mutate(undefined)}>
      {label}
    </Button>
  );
}
